import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SDKStudy } from '@axon/rosetta-sdk';
import { FirebaseAppContext } from 'libs.firebase_react';
import { supportsSimplifiedWebViewportLabels } from 'libs.irb.common';
import { Checkbox, FormSections, InlineNotification, ModalWindow, Spinner, useLocalizeMessage, } from 'libs.nucleus';
import { getConfigLocaleReadiness, useL10nMapFetch } from 'libs.translation_workbench';
import { EnvironmentSelector } from '../environment_selector';
import { InputFieldLabel } from '../input_field_label';
import { DeviceSelection } from './device_selection.component';
import { LocalesSelection } from './locales_selection.component';
import { StudyVersionsDropdown } from './study_versions_dropdown.component';
export const CriteriaSelectionModal = ({ onModalClose, onHandleCreatePackage, isProcessing, versions, }) => {
    const { studyId } = useParams();
    const { logEvent } = useContext(FirebaseAppContext);
    const translate = useLocalizeMessage();
    const defaultSelectedStudyVersion = useMemo(() => versions.values().next().value, [versions]);
    const [selectedEnvironment, setSelectedEnvironment] = useState(undefined);
    const [isCoverPageIncluded, setIsCoverPageIncluded] = useState(true);
    const [isClinicianInterviewClinrosIncluded, setIsClinicianInterviewClinrosIncluded] = useState(true);
    const [selectedLocales, setSelectedLocales] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('web');
    const [selectedStudyVersion, setSelectedStudyVersion] = useState(defaultSelectedStudyVersion);
    const [selectedViewport, setSelectedViewport] = useState({
        width: 1920,
        height: 1080,
        scale: 0.5,
    });
    const selectedStudy = useMemo(() => {
        if (!selectedStudyVersion) {
            return undefined;
        }
        // Ensure meta has hasMixedPIISupport property for backward compatibility
        const studyData = {
            ...selectedStudyVersion.data,
            meta: {
                ...selectedStudyVersion.data.meta,
                hasMixedPIISupport: selectedStudyVersion.data.meta?.hasMixedPIISupport ?? false,
            },
        };
        return SDKStudy.fromRosetta(studyData);
    }, [selectedStudyVersion]);
    // The selected study version's translation state lives in the pinned L10N map resource it
    // references, not in live activity localization documents.
    const { data: l10nMap, isLoading: isL10nMapLoading } = useL10nMapFetch(selectedStudy?.l10nMapResourceId ?? '');
    const l10nMapEntries = useMemo(() => (Array.isArray(l10nMap) ? l10nMap : []), [l10nMap]);
    const localeReadiness = useMemo(() => (selectedStudy ? getConfigLocaleReadiness(selectedStudy, l10nMapEntries) : undefined), [selectedStudy, l10nMapEntries]);
    const availableLocales = useMemo(() => {
        if (!selectedStudy) {
            return [];
        }
        return Array.from(new Set([...selectedStudy.participantLocales, ...selectedStudy.siteLocales]));
    }, [selectedStudy]);
    const isLocalizationDisabled = Boolean(selectedStudy) && localeReadiness?.localizationEnabled === false;
    const useSimplifiedWebLabels = useMemo(() => {
        if (!selectedStudy) {
            return false;
        }
        return supportsSimplifiedWebViewportLabels(selectedStudy.pinnedProductTemplate);
    }, [selectedStudy]);
    const handleModalCancel = useCallback(() => {
        logEvent('submission_package_modal_cancel', {
            studyId,
        });
        onModalClose();
    }, [studyId]);
    const handleModalSubmit = useCallback(() => {
        logEvent('submission_package_modal_submit', {
            studyId,
            selectedLocales: selectedLocales.join(', '),
            selectedDevice,
            selectedEnvironment: selectedEnvironment.name,
            isCoverPageIncluded,
            includeClinicianInterviewClinros: isClinicianInterviewClinrosIncluded,
        });
        onHandleCreatePackage({
            selectedLocales,
            selectedDevice,
            selectedEnvironment,
            isCoverPageIncluded,
            includeClinicianInterviewClinros: isClinicianInterviewClinrosIncluded,
            selectedViewport,
        });
    }, [
        selectedLocales,
        selectedDevice,
        selectedEnvironment,
        isCoverPageIncluded,
        isClinicianInterviewClinrosIncluded,
        selectedViewport,
    ]);
    const handleSelectedStudyVersion = useCallback((studyVersion) => {
        logEvent('submission_package_modal_select_study_version', {
            studyId,
            studyVersionName: studyVersion?.metadata?.name,
            studyVersion: studyVersion?.externalVersion,
        });
        setSelectedStudyVersion(studyVersion);
        setSelectedLocales([]);
    }, []);
    const handleSelectedEnvironment = useCallback((env) => {
        logEvent('submission_package_modal_select_environment', {
            studyId,
            environmentName: env?.name,
            environmentId: env?.id,
        });
        setSelectedEnvironment(env);
    }, []);
    const handleSelectedDevice = useCallback((device, viewport) => {
        logEvent('submission_package_modal_select_device', {
            studyId,
            device,
            viewport,
        });
        setSelectedDevice(device);
        setSelectedViewport(viewport);
    }, []);
    const handleSelectedLocales = useCallback((locales) => {
        logEvent('submission_package_modal_select_locales', {
            studyId,
            locales: locales.join(', '),
        });
        setSelectedLocales(locales);
    }, []);
    const closeButton = {
        label: translate('Cancel'),
        onClick: handleModalCancel,
    };
    const doneButton = {
        label: translate('Create'),
        disabled: isProcessing || selectedLocales.length === 0 || !selectedEnvironment || isLocalizationDisabled,
        onClick: handleModalSubmit,
    };
    const formSections = useMemo(() => [
        {
            title: translate('Settings'),
            content: (_jsxs("div", { className: 'flex gap-6 flex-col', children: [_jsx("div", { children: _jsx(StudyVersionsDropdown, { setSelectedStudyVersion: handleSelectedStudyVersion, selectedStudyVersion: selectedStudyVersion, versions: versions }) }), _jsx("div", { children: _jsx(EnvironmentSelector, { withCortexAccess: true, onEnvSelection: handleSelectedEnvironment, selectedStudyVersion: selectedStudyVersion }) }), _jsxs("div", { "data-testid": 'device-size-dropdown', children: [_jsx(InputFieldLabel, { label: translate('Device Size') }), _jsx(DeviceSelection, { onDeviceSelection: handleSelectedDevice, useSimplifiedWebLabels: useSimplifiedWebLabels })] }), _jsx("div", { children: _jsx(Checkbox, { label: translate('Include cover page, instructions, and table of contents'), description: translate('The cover page provides a general overview including the locale, device size, creation date, and build version. Instructions will help readers understand how to navigate the package, and the table of contents offers reference points for reviewing each screen.'), onChange: (value) => setIsCoverPageIncluded(value), checked: isCoverPageIncluded }) }), _jsx("div", { children: _jsx(Checkbox, { label: translate('Include Clinician Interview ClinROs'), description: translate('Includes all activities and display names for ClinROs where “Clinician Interview” is set to Yes.'), onChange: (value) => setIsClinicianInterviewClinrosIncluded(value), checked: isClinicianInterviewClinrosIncluded }) })] })),
        },
        {
            title: translate('Locales'),
            description: translate('Select from the completed "ready" locale sets. In progress sets become available when finished.'),
            content: (_jsx("div", { className: 'flex gap-6 flex-col', children: isL10nMapLoading ? (_jsx("div", { className: 'flex justify-center py-8', children: _jsx(Spinner, {}) })) : isLocalizationDisabled ? (_jsx(InlineNotification, { title: translate('Translations not enabled in selected study version'), subtitle: _jsxs("div", { className: 'flex flex-col gap-3', children: [_jsx("p", { className: 'm-0', children: translate('Translations were not enabled when this study version was created.') }), _jsx("p", { className: 'm-0', children: _jsx("strong", { children: translate('To proceed, please:') }) }), _jsxs("ul", { className: 'list-disc pl-5 m-0 flex flex-col gap-1', children: [_jsx("li", { children: translate('Select a study version where translations are enabled, or') }), _jsx("li", { children: translate('Ensure translations are enabled for the study') }), _jsx("li", { children: translate('Create a new study version') }), _jsx("li", { children: translate('Create or update a test deployment to use the new version') })] })] }), type: 'error' })) : (_jsxs(_Fragment, { children: [_jsx(InputFieldLabel, { label: translate('Available locales') }), _jsx(LocalesSelection, { onLocaleSelection: handleSelectedLocales, locales: availableLocales, isLocaleReady: localeReadiness ? localeReadiness.isLocaleReady : () => false })] })) })),
        },
    ], [
        isClinicianInterviewClinrosIncluded,
        isCoverPageIncluded,
        selectedStudyVersion,
        versions,
        availableLocales,
        localeReadiness,
        isLocalizationDisabled,
        isL10nMapLoading,
    ]);
    return (_jsx(ModalWindow, { title: translate('Create review package'), isOpen: true, closeWindow: onModalClose, footerPrimaryActionButton: doneButton, footerSecondaryActionButtons: [closeButton], position: 'top', width: 'full', children: _jsxs("section", { className: 'px-8 py-2', children: [_jsx(InlineNotification, { title: translate('Note'), subtitle: translate('The review package will have all screens that a participant could see. Examples are : Sign-in, Authentication, Schedule, Activities, Notifications, Profile, Televisit and Resources.'), type: 'info' }), _jsx("hr", { className: 'mb-4' }), _jsx(FormSections, { sections: formSections }), isProcessing && (_jsx("div", { className: 'absolute inset-0 bg-neutral-900 bg-opacity-50 flex items-center justify-center z-10', children: _jsx(Spinner, {}) }))] }) }));
};
